import { ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict';
import { useCallback, useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { Checkbox, Field, Input, Textarea } from '../../../components/forms';
import { linkClassName } from '../../../components/links';
import { StoreLabelsInput } from '../../../components/products/store-labels-input';
import { AlertContext } from '../../../contexts/alert-context';
import type { Product } from '../../../data/product';
import { useProducts } from '../../../hooks/products';
import { DisabledUntilPicker } from './disabled-until-picker';

type FormValues = {
  disabled: boolean;
  disabled_until: Date | null;
  min_qoh: number;
  notes: string;
  label_ids: number[];
};

// form validation rules
const ValidationSchema = Yup.object().shape({
  disabled: Yup.boolean().required('Disabled is required'),
  disabled_until: Yup.date().nullable().default(null),
  min_qoh: Yup.number().required('Minimum QOH is required'),
  notes: Yup.string().defined(),
  label_ids: Yup.array().of(Yup.number().defined()).defined(),
});

export function EditProduct({
  product,
  onClose,
  onSubmittingChange,
}: {
  product: Product;
  onClose: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
}) {
  const { addSuccessAlert } = useContext(AlertContext);
  const { updateProduct } = useProducts();

  // functions to build form returned by useForm() hook
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
    formState,
  } = useForm<FormValues>({
    resolver: yupResolver(ValidationSchema),
    defaultValues: {
      ...product,
      label_ids: product.label_ids,
      disabled_until: product.disabled_until ?? null,
    },
  });

  useEffect(() => {
    onSubmittingChange?.(formState.isSubmitting);
  }, [formState.isSubmitting, onSubmittingChange]);

  const isDisabled = watch('disabled');
  const disabledUntil = watch('disabled_until');

  const onSubmit = useCallback(
    (data: FormValues) => {
      const updatedProduct = {
        ...product,
        ...data,
        // Clear disabled_until when re-enabling
        disabled_until: data.disabled ? data.disabled_until : null,
      };

      updateProduct(updatedProduct).then(() => {
        addSuccessAlert(`${updatedProduct.brand} | ${updatedProduct.description} updated`, {
          keepAfterRouteChange: true,
          autoCloseAfterTimeMS: 15_000,
        });
        onClose();
      });
    },
    [updateProduct, product, onClose, addSuccessAlert]
  );

  const editStoreLinkParams = { storeId: product.store_id };

  return (
    <form
      id="edit-product-form"
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => reset()}
      className="flex flex-col gap-4 w-full"
    >
      <Field
        label={`Disabled${product.disabled && product.disabled_toggled_at ? ` (since ${formatDistanceToNowStrict(product.disabled_toggled_at, { addSuffix: true })})` : ''}`}
        name="disabled"
        errorMessage={errors.disabled?.message}
      >
        <Checkbox {...register('disabled')} invalid={!!errors.disabled} />
      </Field>

      {isDisabled && (
        <Field
          label={`Disabled until${disabledUntil ? ` (${format(disabledUntil, 'MMMM d, yyyy')})` : ''}`}
          name="disabled_until"
        >
          <Controller
            name="disabled_until"
            control={control}
            render={({ field }) => (
              <DisabledUntilPicker value={field.value} onChange={field.onChange} />
            )}
          />
        </Field>
      )}

      <Field label="Minimum QOH" name="min_qoh" errorMessage={errors.min_qoh?.message}>
        <Input
          {...register('min_qoh', { valueAsNumber: true })}
          type="number"
          invalid={!!errors.min_qoh}
          min="0"
        />
      </Field>

      <Field label="Labels" name="label_ids" errorMessage={errors.label_ids?.message}>
        <Controller
          name="label_ids"
          control={control}
          render={({ field }) => (
            <StoreLabelsInput value={field.value ?? []} onChange={field.onChange} />
          )}
        />
        <Link
          to="/stores/$storeId/edit"
          params={editStoreLinkParams}
          className={`${linkClassName} flex items-center gap-1 justify-self-end`}
        >
          Edit Labels <ChevronDoubleRightIcon className="size-4" />
        </Link>
      </Field>

      <Field label="Notes" name="notes" errorMessage={errors.notes?.message}>
        <Textarea {...register('notes')} invalid={!!errors.notes} />
      </Field>
    </form>
  );
}
